import { FormatLocaleDefinition } from 'd3-format';
import { getUserCulture } from 'utils/UserCulture';

// seems brittle, but that's how it is in legacy...
const NUM_FORMAT_SPECIFIER_INDEX = 3;

export type NumberFormatOption = 'Fixed' | 'Number' | 'Percent' | 'Currency';

export function getCodeForSpecifier(term: string) {
  // this seems naive also, but I didn't see any i18n of legacy editor
  return term[0];
}

export function getNumberFormatOptions(): NumberFormatOption[] {
  return ['Fixed', 'Number', 'Percent', 'Currency'];
}

export function getValueForFormatParts(format: string, places: string, append: string) {
  places = places === 'Default' ? '' : places;
  return `{0:${getCodeForSpecifier(format)}${places}}${append}`;
}

export function getNumberFormatSpecifier(code: string) {
  // these should be an enum, but this is how it is in legacy:
  switch (code) {
    case 'F':
      return 'Fixed';
    case 'N':
      return 'Number';
    case 'P':
      return 'Percent';
    case 'C':
      return 'Currency';
    default:
      return '';
  }
}

export function getPartsFromValue(value: string) {
  const defaults = {
    format: 'Fixed',
    places: 'Default',
    append: '',
  };

  if (!value) {
    return defaults;
  }
  const format: NumberFormatOption | '' = getNumberFormatSpecifier(value[NUM_FORMAT_SPECIFIER_INDEX]);
  const endIndex = value.lastIndexOf('}');
  const append = endIndex !== value.length - 1 ? value.substring(endIndex + 1, value.length) : '';
  const placesIndex = NUM_FORMAT_SPECIFIER_INDEX + 1;
  const places = value.length > 4 && !isNaN(parseInt(value[placesIndex])) ? value[placesIndex] : 'Default';

  return {
    format,
    places,
    append,
  };
}

export function getDecimalPlaceOptions() {
  const decimals = ['Default'];
  for (let i = 0; i < 10; i++) {
    decimals.push(i.toString());
  }
  return decimals;
}

interface ID3FormatStringGetterOptions {
  formatString?: string;
  /** override the type in some cases  */
  typeOverride?: string;
  /** true to use fixed for formats that otherwise wouldn't, e.g. number or currency */
  useLong?: boolean;
  /** zero/ignored by default */
  minPrecision?: number;
}

/**
 * Get a D3.js format string from a decisions format string.
 *
 * https://github.com/d3/d3-format
 * https://observablehq.com/@d3/d3-format
 *
 *
 * this may have some charts oriented assumptions.
 * @param formatString decisions number format string.
 * @param typeOverride d3 type to use if override is desired
 */
export function getD3FormatString({
  formatString = '{0:N2}',
  typeOverride,
  useLong,
  minPrecision = 0,
}: ID3FormatStringGetterOptions) {
  const { format, places } = getPartsFromValue(formatString);

  let type = '';

  switch (format as NumberFormatOption) {
    case 'Fixed':
      type = 'f';
      break;
    case 'Number':
      type = useLong ? 'f' : 's';
      break;
    case 'Percent':
      type = '%';
      break;
    case 'Currency':
      type = useLong ? 'f' : 's';
      break;
    default:
      return '';
  }

  type = typeOverride || type;

  const isCurrency = format === 'Currency';
  // take the greater of override precision or configured precision:
  // overridePrecision is intended for edge cases, e.g. unexpected rounding in chart axis step-value
  const precision = places === 'Default' ? 2 : Math.max(minPrecision, parseInt(places, 10));
  const currency = isCurrency ? '$' : '';
  return `${currency},.${precision}~${type}`;
}

export function getD3Locale(): FormatLocaleDefinition {
  // getUserCulture().UserCulture; -- use this to load from D3 if possible?
  // would get us more accurate groupings and currency position.
  // TODO should incorporate this when passing a function, instead of a string
  const localeFormat = {
    decimal: getUserCulture().NumberDecimalSeparator,
    thousands: getUserCulture().NumberGroupSeparator,
    grouping: [3], // can specify  grouping size array, for example India.
    currency: [getUserCulture().CurrencySymbol, ''] as [string, string], // currently symbol start or end.
  };

  return localeFormat;
}
